// Simplified from LC2 (src/routes/ThemeRoutes.js) — same auth-gating logic. The
// authenticated branch renders the new-UI MainLayout shell (Sidebar + TopHeader) with
// account/community/campaign routes pointed at DashboardPage. Profile/Settings/Resident
// pages aren't ported yet.
import React from 'react';
import {useSelector} from 'react-redux';
import {Navigate, useRoutes} from 'react-router-dom';

import {getAccountSfid, getAuthorizedStatus} from '../app/store/reducers/session';
import {getCookie} from '../commons/utils/cookies';
import MinimalLayout from '../layout/MinimalLayout';
import MainLayout from '../layout/MainLayout/MainLayout';
import ProtectedRoute from './ProtectedRoute';
import LoginRoutes from './LoginRoutes';
import PostLoginPlaceholder from '../pages/PostLoginPlaceholder';
import DashboardPage from '../pages/Dashboard/DashboardPage';
import SettingsLayout from '../layout/SettingsLayout/SettingsLayout';
import ProfileSettingsPage from '../pages/Settings/ProfileSettingsPage';
import SecuritySettingsPage from '../pages/Settings/SecuritySettingsPage';
import MembersSettingsPage from '../pages/Settings/MembersSettingsPage';

// Same account/community/campaign param shape as LC2's MainRoutes.js — pointed at the
// new DashboardPage for all three levels (LC2 used <HomeTabs/> for all three too, letting
// the `openItem`/params decide what's shown inside).
const accountRoutes = [
    {path: 'account/:account_sfid', element: <DashboardPage />},
    {path: 'account/:account_sfid/community/:community_sfid', element: <DashboardPage />},
    {path: 'account/:account_sfid/community/:community_sfid/campaign/:campaign_sfid', element: <DashboardPage />}
];

// Same as LC2's ThemeRoutes: '/' redirects to an account — preferring the last-used one in
// localStorage (set by MainLayout on every account_sfid route visit), falling back to the
// Redux session's accountSfid (populated by getSessionInfo/getTeamMemberInfo right after
// login, before any account_sfid route has ever been visited — without this fallback,
// first-time login after the magic link always got stuck on the placeholder below since
// localStorage is empty until an account route is actually visited once).
const IndexRedirect = () => {
    const sessionAccountSfid = useSelector(getAccountSfid);
    const storedAccountSfid = localStorage.getItem('account_sfid');
    const userAccountSfid = storedAccountSfid || sessionAccountSfid;
    return userAccountSfid ? <Navigate to={`/account/${userAccountSfid}`} replace /> : <PostLoginPlaceholder />;
};

const AppRoutes = () => {
    const isAuthorized = useSelector(getAuthorizedStatus);
    const sessionToken = getCookie('session_token');
    const isAuthenticated = Boolean(isAuthorized || sessionToken);

    const publicPaths = ['/login', '/verify_login', '/verify_sso_login'].map((path) => ({
        path,
        element: <Navigate to="/" replace />
    }));

    const routesConfig = [
        {
            path: '/',
            element: isAuthenticated ? (
                <ProtectedRoute>
                    <MainLayout />
                </ProtectedRoute>
            ) : (
                <MinimalLayout />
            ),
            children: isAuthenticated
                ? [{path: '/', element: <IndexRedirect />}, ...accountRoutes, ...publicPaths]
                : [{path: '/', element: <Navigate to="/login" replace />}, ...LoginRoutes]
        },
        // Settings has its own shell (SettingsLayout) instead of MainLayout's account-scoped
        // Sidebar/TopHeader, so it's a sibling of the '/' branch rather than nested under it.
        // Security/Members pages are placeholders — their Figma designs aren't ported yet —
        // but the routes must exist or the catch-all '*' below bounces the nav back to '/'.
        ...(isAuthenticated
            ? [
                  {
                      path: 'settings',
                      element: (
                          <ProtectedRoute>
                              <SettingsLayout />
                          </ProtectedRoute>
                      ),
                      children: [
                          {path: 'profile', element: <ProfileSettingsPage />},
                          {path: 'security', element: <SecuritySettingsPage />},
                          {path: 'members', element: <MembersSettingsPage />}
                      ]
                  }
              ]
            : []),
        {path: '*', element: <Navigate to="/" replace />}
    ];

    return useRoutes(routesConfig);
};

export default AppRoutes;
